import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { login } from "../DAL/Login/Login";
import { LoginForm } from "../components/authentication/login";
import { get_app_logo } from "../config/config";
import {
  ContentSettingApi,
  navbarListAPi,
} from "src/DAL/ContentSetting/ContentSetting";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { _get_is_seen_video_localStorage } from "src/DAL/localStorage/localStorage";
import GrAuthLayout from "src/design-system/components/GrAuthLayout";
import {
  AUTH_BRAND_DESCRIPTION,
  AUTH_BRAND_HEADING,
  AUTH_BRAND_HIGHLIGHTS,
  AUTH_BRAND_MARK,
  AUTH_BRAND_MARK_NAME,
  AUTH_BRAND_MARK_SUFFIX,
} from "./authBrand";
import { cleanHtml } from "src/utils/cleanHtml";

// "© 2026 GymRite. All rights reserved." with this year (the admin's text carried an old year).
export const copyrightLine = (brandName) =>
  `© ${new Date().getFullYear()} ${
    brandName || "GymRite"
  }. All rights reserved.`;

const safePostLoginPath = (raw) => {
  const value = String(raw || "").trim();
  if (!value.startsWith("/") || value.startsWith("//")) return "/dashboard";
  if (value.startsWith("/login")) return "/dashboard";
  return value;
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const postLoginPath = safePostLoginPath(searchParams.get("redirect"));
  const {
    handleContentSettingData,
    handleDashboardSettingData,
    handleDashboardTestimonialData,
    handleDashboardEventData,
    handleAdminTimeZone,
    handleUserInfo,
    projectInfo,
    handleNavbarList,
    START_SOCKET_FORCEFULLY,
  } = useContentSetting();

  const is_seen_video = _get_is_seen_video_localStorage();

  const getContentSetting = async () => {
    const navbar_result = await navbarListAPi();
    if (navbar_result.code === 200) {
      handleNavbarList({
        ...navbar_result.menus,
        ...navbar_result?.booking_information,
      });
      localStorage.setItem(
        `navbar_list`,
        JSON.stringify({
          ...navbar_result.menus,
          ...navbar_result?.booking_information,
        })
      );
    }
    const result = await ContentSettingApi();
    if (result.code === 200) {
      if (result.content_setting === null) {
        handleDashboardSettingData({});
        handleContentSettingData({});
        handleDashboardTestimonialData([]);
        handleDashboardEventData([]);
        handleAdminTimeZone("");
        return;
      }
      localStorage.setItem(
        `content_setting`,
        JSON.stringify(result?.content_setting?.client_content_setting)
      );
      localStorage.setItem(
        `client_dashboard_setting`,
        JSON.stringify(result?.content_setting?.client_dashboard_setting)
      );

      localStorage.setItem(`testmonial`, JSON.stringify(result?.testmonial));
      localStorage.setItem(`home_event`, JSON.stringify(result?.home_event));
      localStorage.setItem(
        `admin_time_zone`,
        JSON.stringify(result?.time_zone)
      );
      handleAdminTimeZone(result.time_zone);
      handleDashboardEventData(result.home_event);
      handleDashboardTestimonialData(result.testmonial);
      handleContentSettingData(result.content_setting.client_content_setting);
      handleDashboardSettingData(
        result.content_setting.client_dashboard_setting
      );

      navigate(postLoginPath, { replace: true });
      setLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleSubmitLogin = async (data) => {
    setLoading(true);
    const result = await login(data);
    if (result.code === 200) {
      localStorage.setItem(`user_data`, JSON.stringify(result.member));
      if (result?.member?.target_weight) {
        localStorage.setItem("unit_added", true);
      }
      localStorage.setItem(`user_data_new`, JSON.stringify(result.member));
      handleUserInfo(result?.member);
      START_SOCKET_FORCEFULLY(result?.member?._id);
      localStorage.setItem("token", result.token);
      if (is_seen_video && is_seen_video === "Yes") {
      } else {
        localStorage.setItem("is_seen_video", "No");
      }
      getContentSetting();
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setLoading(false);
    }
  };

  // The gym's own HTML (admin settings), cleaned before it is shown (rule 18).
  const adminHtml = (html) =>
    html ? (
      <Box
        component="span"
        dangerouslySetInnerHTML={{ __html: cleanHtml(html) }}
      />
    ) : null;

  return (
    <GrAuthLayout
      brandMark={AUTH_BRAND_MARK}
      brandMarkName={AUTH_BRAND_MARK_NAME}
      brandMarkSuffix={AUTH_BRAND_MARK_SUFFIX}
      heading={AUTH_BRAND_HEADING}
      description={AUTH_BRAND_DESCRIPTION}
      highlights={AUTH_BRAND_HIGHLIGHTS}
      appStrip
      formLogo={get_app_logo()}
      legal={copyrightLine(projectInfo?.brand_name)}
      title={adminHtml(projectInfo?.welcome_login_page_text)}
      subtitle={adminHtml(projectInfo?.login_page_text)}
    >
      <LoginForm isLoading={loading} onhandleLogin={handleSubmitLogin} />
    </GrAuthLayout>
  );
}
